import type { PropsWithChildren } from 'react'

// Stores
import { useAuthStoreActions } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { FEATURES } from '@/stores/features'

// Components
import { Button } from '@/components/base/button'
import { Icon } from '@/components/common/icons/Icon'
import { Svg } from '@/components/common/icons/Svg'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/base/avatar'
import { ScrollArea } from '@/components/base/scroll-area'
import { Separator } from '@/components/base/separator'
import { DialogDeveloperCall } from '@/components/DialogDeveloperCall'
import { UserProfile } from '@/components/UserProfile'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTrigger,
} from '@/components/base/sheet'
import { Feature } from '@/components/Feature'

// Assets
import checked from '@/assets/checked.png'
import { UserSettingsDevices } from './UserSettingsDevices'

type SidebarSettingsProps = PropsWithChildren

export const SidebarSettings = ({ children }: SidebarSettingsProps) => {
  const viewElementRef = useUiStore(state => state.viewElementRef)
  const { logout } = useAuthStoreActions()

  return (
    <Sheet>
      <SheetTrigger data-testid="sidebar-trigger">{children}</SheetTrigger>
      <SheetContent
        className="ml-auto flex h-full w-[305px] shrink-0 flex-col gap-0 overflow-hidden px-2 py-3 text-secondary"
        container={viewElementRef?.current}
        side="left"
      >
        <SheetHeader className="flex flex-col gap-y-1">
          <div className="py-2 pl-4">
            <Svg tag="sw-logotype" />
            <span className="sr-only">SignalWire</span>
          </div>
          <div className="flex items-center gap-x-2.5 py-2 pl-4 text-lg font-semibold text-foreground">
            <Avatar className="h-6 w-6">
              <AvatarImage src={checked} />
              <AvatarFallback className="bg-primary">CN</AvatarFallback>
            </Avatar>
            <UserProfile />
          </div>
          <SheetDescription className="hidden" />
        </SheetHeader>
        <ScrollArea className="m-0 flex flex-col gap-y-1 pl-4 pr-3 text-sm text-foreground">
          <h2 className="py-4">Settings</h2>
          <UserSettingsDevices />
          <Feature name={FEATURES.DEVELOPER_TOOLS}>
            <Separator className="my-5 bg-primary-light" />
            <h2 className="py-5" data-testid="developer-tools">
              Developer
            </h2>
            <ul>
              <li className="flex items-center space-x-3 py-5">
                <Icon tag="call" size="lg" variant="foreground" />
                <DialogDeveloperCall>
                  <div>Make a Call</div>
                </DialogDeveloperCall>
              </li>
            </ul>
            <Separator className="my-5 bg-primary-light" />
          </Feature>
          <div className="py-4">
            <Button
              className="p-0 pr-2 text-destructive"
              variant="icon"
              onClick={logout}
            >
              <Icon tag="keyboard-left-arrow" size="xl" />
              <div>Logout</div>
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
